import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FaCalendarAlt,
  FaPlus,
  FaPhoneAlt,
  FaCheck,
  FaTimes,
} from 'react-icons/fa';
import { crmFollowupApi } from '../../api/crmApi';
import { LeadStatusBadge, PriorityBadge } from './components/LeadStatusBadge';
import { FollowupModal } from './components/FollowupModal';
import AnalogTimePicker from './components/AnalogTimePicker';
import './CRM.css';

const fallbackToday = [
  { id: 1, leadId: 1, lead: 'Ankit Mehta', company: 'Mehta Oxygen Pvt. Ltd.', type: 'Call', scheduledAt: '2026-10-04T10:30:00', priority: 'High', status: 'Pending', notes: 'Discuss quotation for 50 cylinders' },
  { id: 2, leadId: 2, lead: 'Rajesh Kumar', company: 'Kumar Industrial Gases', type: 'WhatsApp', scheduledAt: '2026-10-04T11:00:00', priority: 'Medium', status: 'Pending', notes: 'Send product brochure' },
  { id: 3, leadId: 3, lead: 'Sunita Devi', company: 'Devi Gas Agency', type: 'Quotation Follow-up', scheduledAt: '2026-10-04T14:00:00', priority: 'High', status: 'Pending', notes: 'Follow up on quotation QT-2041' },
];
const fallbackUpcoming = [
  { id: 4, leadId: 4, lead: 'Mohammed Ali', company: 'Ali Welding Works', type: 'Meeting', scheduledAt: '2026-10-06T12:00:00', priority: 'Medium', status: 'Pending', notes: 'On-site requirement discussion' },
  { id: 5, leadId: 5, lead: 'Pooja Sharma', company: 'Sharma Medical Gas', type: 'Call', scheduledAt: '2026-10-07T15:00:00', priority: 'High', status: 'Pending', notes: 'Negotiate bulk pricing' },
];
const fallbackOverdue = [
  { id: 6, leadId: 6, lead: 'Deepak Joshi', company: 'Joshi Fabricators', type: 'Payment Follow-up', scheduledAt: '2026-10-01T10:00:00', priority: 'High', status: 'Overdue', notes: 'Payment pending for SO-1180' },
];

const timeOf = (d) => d ? new Date(d).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '';
const dateOf = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '';
const pad2 = (n) => String(n).padStart(2, '0');
const toTimeInput = (d) => {
  if (!d) return '10:00';
  const t = new Date(d);
  return `${pad2(t.getHours())}:${pad2(t.getMinutes())}`;
};

const SkeletonBlock = ({ width = '100%', height = 16, radius = 8, style = {} }) => (
  <span className="crm-skeleton" style={{ width, height, borderRadius: radius, ...style }} />
);

export default function CRMFollowups() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('today');
  const [loading, setLoading] = useState(true);
  const [today, setToday] = useState(fallbackToday);
  const [upcoming, setUpcoming] = useState(fallbackUpcoming);
  const [overdue, setOverdue] = useState(fallbackOverdue);
  const [modal, setModal] = useState({ open: false, followup: null });
  const [reschedule, setReschedule] = useState(null);
  const [resDate, setResDate] = useState('');
  const [resTime, setResTime] = useState('');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [t, u, o] = await Promise.allSettled([crmFollowupApi.getToday(), crmFollowupApi.getUpcoming({ size: 50 }), crmFollowupApi.getOverdue()]);
      if (t.status === 'fulfilled' && Array.isArray(t.value?.data)) setToday(t.value.data);
      if (u.status === 'fulfilled' && Array.isArray(u.value?.data)) setUpcoming(u.value.data);
      if (o.status === 'fulfilled' && Array.isArray(o.value?.data)) setOverdue(o.value.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const complete = async (f) => {
    try { await crmFollowupApi.complete(f.id, { completedAt: new Date().toISOString() }); toast.success('Follow-up completed.'); }
    catch { toast.error('Failed to complete follow-up.'); }
    setToday((p) => p.filter((x) => x.id !== f.id));
    setUpcoming((p) => p.filter((x) => x.id !== f.id));
    setOverdue((p) => p.filter((x) => x.id !== f.id));
  };

  const openReschedule = (f) => {
    setResDate((f.scheduledAt || '').slice(0, 10));
    setResTime(toTimeInput(f.scheduledAt));
    setReschedule(f);
  };

  const saveReschedule = async () => {
    if (!resDate) { toast.error('Please choose a date.'); return; }
    try {
      await crmFollowupApi.reschedule(reschedule.id, { scheduledAt: `${resDate}T${resTime || '10:00'}:00` });
      toast.success('Follow-up rescheduled.');
      setReschedule(null);
      fetchAll();
    } catch {
      toast.error('Failed to reschedule.');
    }
  };

  const lists = { today, upcoming, overdue };
  const current = lists[tab] || [];

  const renderRow = (f) => (
    <tr key={f.id}>
      <td className="link-cell" onClick={() => f.leadId && navigate(`/crm/leads/${f.leadId}`)}>
        <strong>{f.lead || f.leadName}</strong>
        <div style={{ fontSize: '0.72rem', color: 'var(--ink-soft)' }}>{f.company}</div>
      </td>
      <td>{f.type}</td>
      <td style={{ whiteSpace: 'nowrap' }}>
        {tab === 'today' ? timeOf(f.scheduledAt) : dateOf(f.scheduledAt)}
        {tab === 'overdue' && <div style={{ fontSize: '0.72rem', color: 'var(--danger)' }}>Was: {dateOf(f.scheduledAt)}</div>}
      </td>
      <td><PriorityBadge priority={f.priority} /></td>
      <td><LeadStatusBadge status={f.status === 'Overdue' ? 'Lost' : f.status === 'Completed' ? 'Won' : 'New'} labelOverride={f.status} /></td>
      <td style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={f.notes}>{f.notes || '—'}</td>
      <td>
          <div className="crm-actions">
            <button type="button" className="crm-icon-btn call" title="Call" aria-label={`Call ${f.lead || f.leadName || ''}`} onClick={() => window.location.href = `tel:${f.mobile || ''}`}><FaPhoneAlt aria-hidden="true" /></button>
            <button type="button" className="crm-icon-btn followup" title="Reschedule" aria-label="Reschedule follow-up" onClick={() => openReschedule(f)}><FaCalendarAlt aria-hidden="true" /></button>
            <button type="button" className="crm-icon-btn convert" title="Complete" aria-label="Mark follow-up complete" onClick={() => complete(f)}><FaCheck aria-hidden="true" /></button>
          </div>
      </td>
    </tr>
  );

  return (
    <div className="crm-page crm-followups-page">
      <div className="crm-header">
        <div className="crm-header-left">
          <div className="crm-page-icon" aria-hidden="true"><FaCalendarAlt /></div>
          <div>
            <h1 className="crm-title">Follow-ups</h1>
            <p className="crm-subtitle">Today's tasks, upcoming reminders & overdue items</p>
          </div>
        </div>
        <div className="crm-header-actions">
          <button type="button" className="crm-btn crm-btn-primary" onClick={() => setModal({ open: true, followup: null })}>
            <FaPlus aria-hidden="true" /> New Follow-up
          </button>
        </div>
      </div>

      <div className="crm-kpi-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))' }}>
        {[
          { key: 'today', label: "Today's Follow-ups", value: today.length, color: '#6366F1' },
          { key: 'upcoming', label: 'Upcoming', value: upcoming.length, color: '#0EA5E9' },
          { key: 'overdue', label: 'Overdue', value: overdue.length, color: '#EF4444' },
        ].map((k) => (
          <div className={`crm-kpi-card ${tab === k.key ? 'selected' : ''}`} key={k.key} onClick={() => setTab(k.key)} style={{ cursor: 'pointer', borderColor: tab === k.key ? k.color : undefined }}>
            {loading ? (
              <div style={{ width: '100%' }}>
                <SkeletonBlock width="50%" height={20} style={{ marginBottom: 8 }} />
                <SkeletonBlock width="75%" height={12} radius={6} />
              </div>
            ) : (
              <div>
                <div className="crm-kpi-value" style={{ color: k.color }}>{k.value}</div>
                <div className="crm-kpi-label">{k.label}</div>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="crm-table-wrap">
        <table className="crm-table">
          <thead><tr><th>Lead</th><th>Type</th><th>{tab === 'today' ? 'Time' : 'Scheduled'}</th><th>Priority</th><th>Status</th><th>Notes</th><th>Actions</th></tr></thead>
          <tbody>
            {loading ? (
              Array.from({ length: 6 }).map((_, r) => (
                <tr key={`skeleton-${r}`}>
                  {Array.from({ length: 7 }).map((__, cc) => (
                    <td key={cc}><SkeletonBlock width={cc === 0 ? 130 : '80%'} height={16} radius={6} /></td>
                  ))}
                </tr>
              ))
            ) : (
              <>
            {current.map(renderRow)}
            {!current.length && (
              <tr><td colSpan={7}>
                <div className="crm-empty">
                  <div className="crm-empty-icon" aria-hidden="true"><FaCalendarAlt /></div>
                  <div className="crm-empty-title">Nothing here</div>
                  <div className="crm-empty-desc">{tab === 'overdue' ? 'Great — no overdue follow-ups!' : 'No follow-ups in this list.'}</div>
                </div>
              </td></tr>
            )}
              </>
            )}
          </tbody>
        </table>
      </div>

      {reschedule && createPortal(
        <div className="crm-modal-backdrop" onClick={(e) => e.target === e.currentTarget && setReschedule(null)}>
          <div className="crm-modal" role="dialog" aria-modal="true" aria-label="Reschedule follow-up">
            <div className="crm-modal-header">
              <h3><FaCalendarAlt aria-hidden="true" /> Reschedule Follow-up</h3>
              <button type="button" className="crm-modal-close" onClick={() => setReschedule(null)} aria-label="Close dialog"><FaTimes aria-hidden="true" /></button>
            </div>
            <div className="crm-modal-body">
              <div className="crm-form-grid">
                <div className="crm-field crm-field-full">
                  <label>Lead / Customer</label>
                  <input type="text" readOnly value={reschedule.lead || reschedule.leadName || ''} style={{ cursor: 'default' }} />
                </div>
                <div className="crm-field">
                  <label>Date <span className="req">*</span></label>
                  <input type="date" value={resDate} onChange={(e) => setResDate(e.target.value)} />
                </div>
                <div className="crm-field">
                  <AnalogTimePicker label="Time" value={resTime} onChange={setResTime} />
                </div>
                <div className="crm-field crm-field-full">
                  <label>Notes</label>
                  <input type="text" readOnly value={reschedule.notes || '—'} style={{ cursor: 'default' }} />
                </div>
              </div>
            </div>
            <div className="crm-modal-footer">
              <button type="button" className="crm-btn crm-btn-ghost" onClick={() => setReschedule(null)}>Cancel</button>
              <button type="button" className="crm-btn crm-btn-primary" onClick={saveReschedule}>
                <FaCalendarAlt aria-hidden="true" /> Reschedule
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      <FollowupModal open={modal.open} lead={null} onClose={() => setModal({ open: false, followup: null })}
        onSave={async (form) => {
          try { await crmFollowupApi.create(form); } catch { throw new Error('save-failed'); }
          toast.success('Follow-up created.');
          setModal({ open: false, followup: null });
          fetchAll();
        }} />
    </div>
  );
}
